import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch, FaImage, FaSpinner, FaTimes } from 'react-icons/fa';
import menuApiService from '../services/menuApi';

const MenuManagement = () => {
  // Состояния для данных
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [stats, setStats] = useState({});
  
  // Состояния для UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('dishes'); // 'dishes' или 'categories'
  
  // Выбранное заведение
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  
  // Фильтры и поиск
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  
  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Модальные окна
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add', 'edit', 'view', 'category'
  const [selectedDish, setSelectedDish] = useState(null);
  // Флаг удаления текущего изображения блюда при редактировании
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false);
  
  // Формы
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    old_price: '',
    weight: '',
    calories: '',
    category: '',
    image: null,
    is_available: true,
    is_vegetarian: false,
    is_spicy: false,
    is_new: false,
    is_popular: false,
    order: 0
  });
  
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    establishment: '',
    is_active: true,
    order: 0
  });

  // Загрузка данных
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Явно тянем список заведений с корректного эндпоинта (fallback на старый)
      const token = localStorage.getItem('access_token');
      const headers = token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' };
      let establishmentsResp;
      try {
        const r1 = await fetch('/api/establishments/list/', { headers });
        if (!r1.ok) throw new Error(String(r1.status));
        establishmentsResp = await r1.json();
      } catch (_) {
        try {
          // fallback на старый эндпоинт (требует авторизацию)
          const r2 = await fetch('/api/establishments/', { headers });
          if (!r2.ok) throw new Error(String(r2.status));
          establishmentsResp = await r2.json();
        } catch (_) {
          // последний фолбэк: основной домен (без авторизации)
          try {
            const r3 = await fetch('https://chuduhouse.ru/api/establishments/list/');
            if (!r3.ok) throw new Error(String(r3.status));
            establishmentsResp = await r3.json();
          } catch (e) {
            establishmentsResp = [];
          }
        }
      }
      const estList = Array.isArray(establishmentsResp)
        ? establishmentsResp
        : (establishmentsResp?.results || establishmentsResp?.establishments || establishmentsResp?.data || []);
      setEstablishments(estList);

      // Эффективное выбранное заведение (берём первое, если ещё не выбрано)
      const effectiveEst = selectedEstablishment || (estList.length > 0 ? estList[0] : null);
      if (effectiveEst && (!selectedEstablishment || selectedEstablishment.id !== effectiveEst.id)) {
        setSelectedEstablishment(effectiveEst);
      }

      // Если заведение определено, загружаем данные для него
      if (effectiveEst) {
        const dataPromises = [
          menuApiService.getMenuStats(effectiveEst.id)
        ];

        // Загружаем данные в зависимости от активной вкладки
        if (activeTab === 'dishes') {
          dataPromises.push(
            menuApiService.getAdminDishes({
              establishment: effectiveEst.id,
              search: searchTerm,
              category: selectedCategory !== 'all' ? selectedCategory : undefined,
              is_available: availabilityFilter !== 'all' ? availabilityFilter : undefined,
              page: currentPage,
              page_size: 20
            })
          );
        }
        
        dataPromises.push(menuApiService.getAdminCategories(effectiveEst.id));

        const dataResponses = await Promise.all(dataPromises);
        
        setStats(dataResponses[0] || {});
        
        if (activeTab === 'dishes') {
          const dishesResponse = dataResponses[1];
          setDishes(dishesResponse.results || []);
          setTotalPages(dishesResponse.total_pages || 1);
          setTotalCount(dishesResponse.count || 0);
        }
        
        const categoriesResponse = dataResponses[activeTab === 'dishes' ? 2 : 1];
        let allCategories = categoriesResponse.results || [];
        
        // Фильтрация категорий по поиску
        if (activeTab === 'categories' && searchTerm) {
          allCategories = allCategories.filter(category =>
            category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        }
        
        setCategories(allCategories);
      }
      
      setError(null);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки данных');
      console.error('Ошибка загрузки данных:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, availabilityFilter, currentPage, activeTab, selectedEstablishment]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Автоселект первого заведения при загрузке списка
  useEffect(() => {
    if (!selectedEstablishment && Array.isArray(establishments) && establishments.length > 0) {
      setSelectedEstablishment(establishments[0]);
    }
  }, [establishments, selectedEstablishment]);

  // Обработчики поиска и фильтрации
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Сбрасываем на первую страницу при поиске
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  const handleAvailabilityChange = (e) => {
    setAvailabilityFilter(e.target.value);
    setCurrentPage(1);
  };

  // Пагинация
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Модальные окна
  const openModal = (type, dish = null) => {
    setModalType(type);
    setSelectedDish(dish);
    setRemoveCurrentImage(false);
    
    if (type === 'edit' && dish) {
      setFormData({
        name: dish.name || '',
        description: dish.description || '',
        price: dish.price || '',
        old_price: dish.old_price || '',
        weight: dish.weight || '',
        calories: dish.calories || '',
        category: dish.category || '',
        image: null,
        is_available: dish.is_available || true,
        is_vegetarian: dish.is_vegetarian || false,
        is_spicy: dish.is_spicy || false,
        is_new: dish.is_new || false,
        is_popular: dish.is_popular || false,
        order: dish.order || 0
      });
    } else if (type === 'add') {
      setFormData({
        name: '',
        description: '',
        price: '',
        old_price: '',
        weight: '',
        calories: '',
        category: categories.length > 0 ? categories[0].id : '',
        image: null,
        is_available: true,
        is_vegetarian: false,
        is_spicy: false,
        is_new: false,
        is_popular: false,
        order: 0
      });
    } else if (type === 'category') {
      setCategoryFormData({
        name: '',
        description: '',
        establishment: selectedEstablishment ? selectedEstablishment.id : '',
        is_active: true,
        order: 0
      });
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDish(null);
    setError(null);
  };

  // Обработчики форм
  const handleFormChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCategoryFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setCategoryFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setCategoryFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // CRUD операции для блюд
  const handleSubmitDish = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      // Если пометили удаление изображения и не выбрано новое — отправим сигнал на удаление
      if (modalType === 'edit' && removeCurrentImage && !formData.image) {
        // Backend-совместимый вариант: и пустая строка, и специальный флаг
        formData.image = '';
        formData._remove_image = true;
      }

      if (modalType === 'add') {
        await menuApiService.createDish(formData);
      } else if (modalType === 'edit') {
        await menuApiService.updateDish(selectedDish.id, formData);
      }
      
      await loadData();
      closeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteDish = async (dishId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это блюдо?')) {
      return;
    }
    
    setUpdating(true);
    try {
      await menuApiService.deleteDish(dishId);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // CRUD операции для категорий
  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      await menuApiService.createCategory(categoryFormData);
      await loadData();
      closeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleEditCategory = (category) => {
    setSelectedDish(category); // Используем selectedDish для категории
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      establishment: category.establishment || '',
      is_active: category.is_active || true,
      order: category.order || 0
    });
    setModalType('editCategory');
    setShowModal(true);
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      await menuApiService.updateCategory(selectedDish.id, categoryFormData);
      await loadData();
      closeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
      return;
    }
    
    setUpdating(true);
    try {
      await menuApiService.deleteCategory(categoryId);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleViewCategory = (category) => {
    setSelectedDish(category); // Используем selectedDish для категории
    setModalType('viewCategory');
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-blue-500" />
        <span className="ml-2 text-lg">Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Заголовок и статистика */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <h1 className="text-3xl font-bold text-gray-800">Управление меню</h1>
          
          {/* Селектор заведения */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Заведение:</label>
            <select
              value={selectedEstablishment ? String(selectedEstablishment.id) : ''}
              onChange={(e) => {
                const establishment = (Array.isArray(establishments) ? establishments : [])
                  .find(est => String(est.id) === e.target.value);
                setSelectedEstablishment(establishment);
                setCurrentPage(1); // Сбрасываем пагинацию
                setSearchTerm(''); // Сбрасываем поиск
                setSelectedCategory('all'); // Сбрасываем фильтр категорий
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              <option value="">Выберите заведение</option>
              {Array.isArray(establishments) && establishments.map(establishment => (
                <option key={establishment.id} value={String(establishment.id)}>
                  {establishment.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Статистика */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{stats.total_dishes || 0}</div>
            <div className="text-sm text-blue-500">Всего блюд</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-600">{stats.active_dishes || 0}</div>
            <div className="text-sm text-green-500">Активных блюд</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="text-2xl font-bold text-orange-600">{stats.total_categories || 0}</div>
            <div className="text-sm text-orange-500">Категорий</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-2xl font-bold text-purple-600">{stats.inactive_dishes || 0}</div>
            <div className="text-sm text-purple-500">Неактивных блюд</div>
          </div>
        </div>

        {/* Вкладки */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('dishes');
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dishes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Блюда ({stats.total_dishes || 0})
            </button>
            <button
              onClick={() => {
                setActiveTab('categories');
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'categories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Категории ({stats.total_categories || 0})
            </button>
          </nav>
        </div>
      </div>

      {/* Ошибки */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Предупреждение о необходимости выбрать заведение */}
      {!selectedEstablishment && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4">
          Выберите заведение для управления меню
        </div>
      )}

      {/* Панель управления */}
      {selectedEstablishment && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Поиск */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={activeTab === 'dishes' ? "Поиск блюд..." : "Поиск категорий..."}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Фильтры для блюд */}
            {activeTab === 'dishes' && (
              <div className="flex gap-4">
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Все категории</option>
                  {Array.isArray(categories) && categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>

                <select
                  value={availabilityFilter}
                  onChange={handleAvailabilityChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Все блюда</option>
                  <option value="true">Доступные</option>
                  <option value="false">Недоступные</option>
                </select>
              </div>
            )}

            {/* Кнопки действий */}
            <div className="flex gap-2">
              {activeTab === 'categories' && (
                <button
                  onClick={() => openModal('category')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <FaPlus /> Категория
                </button>
              )}
              {activeTab === 'dishes' && (
                <button
                  onClick={() => openModal('add')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FaPlus /> Блюдо
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Информация о результатах */}
        <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600">
          {activeTab === 'dishes' 
            ? `Найдено ${totalCount} блюд • Страница ${currentPage} из ${totalPages}`
            : `Найдено ${categories.length} категорий`
          }
        </div>
      </div>
      )}

      {/* Контент вкладок */}
      {selectedEstablishment && (
        activeTab === 'dishes' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.isArray(dishes) && dishes.map((dish) => (
            <div key={dish.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              {/* Изображение блюда */}
              <div className="relative h-48 bg-gray-100">
                {dish.image ? (
                  <img
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    src={dish.image}
                    alt={dish.name}
                    onError={(e) => {
                      e.target.src = '/api/placeholder/300/200';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FaImage className="text-gray-400 text-4xl" />
                  </div>
                )}
                
                {/* Статус доступности */}
                <div className="absolute top-3 right-3">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium shadow-sm ${
                    dish.is_available
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {dish.is_available ? 'Доступно' : 'Недоступно'}
                  </span>
                </div>

                {/* Бейджи особенностей */}
                <div className="absolute bottom-3 left-3 flex flex-wrap gap-1">
                  {dish.is_vegetarian && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      🌱
                    </span>
                  )}
                  {dish.is_spicy && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                      🌶️
                    </span>
                  )}
                  {dish.is_new && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      ✨
                    </span>
                  )}
                  {dish.is_popular && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                      🔥
                    </span>
                  )}
                </div>
              </div>

              {/* Информация о блюде */}
              <div className="p-4">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{dish.name}</h3>
                  <p className="text-sm text-gray-500 mb-2 h-10 overflow-hidden">{dish.description}</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {dish.category_name}
                  </span>
                </div>

                {/* Цена и вес */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-gray-900">{dish.price} ₽</span>
                    {dish.old_price && (
                      <span className="text-sm text-gray-500 line-through">{dish.old_price} ₽</span>
                    )}
                  </div>
                  {dish.weight && (
                    <span className="text-sm text-gray-500">{dish.weight}</span>
                  )}
                </div>

                {/* Кнопки действий */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openModal('view', dish)}
                    className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                    title="Просмотр"
                  >
                    <FaEye className="text-xs" />
                    Просмотр
                  </button>
                  <button
                    onClick={() => openModal('edit', dish)}
                    className="px-3 py-2 text-sm font-medium text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
                    title="Редактировать"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDeleteDish(dish.id)}
                    className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    title="Удалить"
                    disabled={updating}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(categories) && categories.map((category) => (
            <div key={category.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
              {/* Информация о категории */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                  {/* Статус активности */}
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    category.is_active
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
                    {category.is_active ? 'Активна' : 'Неактивна'}
                  </span>
                </div>
                
                {category.description && (
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{category.description}</p>
                )}
                
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500">{category.establishment_name}</span>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-blue-600">{category.active_dishes_count}</span>
                    <span className="text-gray-400">/{category.dishes_count} блюд</span>
                  </div>
                </div>

                                 {/* Кнопки действий */}
                 <div className="flex items-center gap-2">
                   <button
                     onClick={() => handleViewCategory(category)}
                     className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                   >
                     <FaEye className="text-xs" />
                     Просмотр
                   </button>
                   <button
                     onClick={() => handleEditCategory(category)}
                     className="px-3 py-2 text-sm font-medium text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
                     title="Редактировать"
                   >
                     <FaEdit />
                   </button>
                   <button
                     onClick={() => handleDeleteCategory(category.id)}
                     className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                     title="Удалить"
                     disabled={updating}
                   >
                     <FaTrash />
                   </button>
                 </div>
              </div>
            </div>
          ))}
        </div>
      )
      )}

      {/* Пагинация для блюд */}
      {selectedEstablishment && activeTab === 'dishes' && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6 overflow-hidden">
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Предыдущая
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Следующая
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Показано <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> до{' '}
                    <span className="font-medium">{Math.min(currentPage * 20, totalCount)}</span> из{' '}
                    <span className="font-medium">{totalCount}</span> результатов
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Предыдущая
                    </button>
                    
                    {/* Номера страниц */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Следующая
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальные окна */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Заголовок модального окна */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalType === 'add' && 'Добавить блюдо'}
                  {modalType === 'edit' && 'Редактировать блюдо'}
                  {modalType === 'view' && 'Просмотр блюда'}
                  {modalType === 'category' && 'Добавить категорию'}
                  {modalType === 'editCategory' && 'Редактировать категорию'}
                  {modalType === 'viewCategory' && 'Просмотр категории'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Содержимое модального окна */}
              {modalType === 'viewCategory' && selectedDish && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      {selectedDish.image ? (
                        <img
                          src={selectedDish.image}
                          alt={selectedDish.name}
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = '/api/placeholder/300/200';
                          }}
                        />
                      ) : (
                        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                          <FaImage className="text-gray-400 text-4xl" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Название</label>
                        <p className="text-sm text-gray-900">{selectedDish.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Заведение</label>
                        <p className="text-sm text-gray-900">{selectedDish.establishment_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Количество блюд</label>
                        <p className="text-sm text-gray-900">
                          {selectedDish.active_dishes_count} активных / {selectedDish.dishes_count} всего
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Порядок</label>
                        <p className="text-sm text-gray-900">{selectedDish.order}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Статус</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          selectedDish.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedDish.is_active ? 'Активна' : 'Неактивна'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedDish.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Описание</label>
                      <p className="text-sm text-gray-900">{selectedDish.description}</p>
                    </div>
                  )}
                </div>
              )}

              {modalType === 'view' && selectedDish && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      {selectedDish.image ? (
                        <img
                          src={selectedDish.image}
                          alt={selectedDish.name}
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = '/api/placeholder/300/200';
                          }}
                        />
                      ) : (
                        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                          <FaImage className="text-gray-400 text-4xl" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Название</label>
                        <p className="text-sm text-gray-900">{selectedDish.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Категория</label>
                        <p className="text-sm text-gray-900">{selectedDish.category_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Цена</label>
                        <p className="text-sm text-gray-900">
                          {selectedDish.price} ₽
                          {selectedDish.old_price && (
                            <span className="ml-2 text-gray-500 line-through">
                              {selectedDish.old_price} ₽
                            </span>
                          )}
                        </p>
                      </div>
                      {selectedDish.weight && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Вес/Объем</label>
                          <p className="text-sm text-gray-900">{selectedDish.weight}</p>
                        </div>
                      )}
                      {selectedDish.calories && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Калорийность</label>
                          <p className="text-sm text-gray-900">{selectedDish.calories}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Статус</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          selectedDish.is_available
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedDish.is_available ? 'Доступно' : 'Недоступно'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedDish.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Описание</label>
                      <p className="text-sm text-gray-900">{selectedDish.description}</p>
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    {selectedDish.is_vegetarian && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        🌱 Вегетарианское
                      </span>
                    )}
                    {selectedDish.is_spicy && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        🌶️ Острое
                      </span>
                    )}
                    {selectedDish.is_new && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        ✨ Новинка
                      </span>
                    )}
                    {selectedDish.is_popular && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        🔥 Популярное
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Форма блюда */}
              {(modalType === 'add' || modalType === 'edit') && (
                <form onSubmit={handleSubmitDish} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Название *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Категория *
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Выберите категорию</option>
                        {Array.isArray(categories) && categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Описание
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Цена *
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleFormChange}
                        required
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Старая цена
                      </label>
                      <input
                        type="number"
                        name="old_price"
                        value={formData.old_price}
                        onChange={handleFormChange}
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Вес/Объем
                      </label>
                      <input
                        type="text"
                        name="weight"
                        value={formData.weight}
                        onChange={handleFormChange}
                        placeholder="например: 250г"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Калорийность
                      </label>
                      <input
                        type="text"
                        name="calories"
                        value={formData.calories}
                        onChange={handleFormChange}
                        placeholder="например: 350 ккал"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Порядок сортировки
                      </label>
                      <input
                        type="number"
                        name="order"
                        value={formData.order}
                        onChange={handleFormChange}
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Изображение
                    </label>
                    {/* Превью существующего изображения (если есть) */}
                    {modalType !== 'add' && selectedDish?.image && !removeCurrentImage && (
                      <div className="mt-2 mb-3 relative inline-block">
                        <img
                          src={selectedDish.image}
                          alt={selectedDish.name}
                          className="h-24 w-24 object-cover rounded-lg border"
                          onError={(e) => { e.target.src = '/api/placeholder/150/150'; }}
                        />
                        <button
                          type="button"
                          onClick={() => setRemoveCurrentImage(true)}
                          title="Удалить изображение"
                          className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full w-7 h-7 flex items-center justify-center shadow-sm hover:bg-gray-50"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    {modalType !== 'add' && selectedDish?.images && Array.isArray(selectedDish.images) && selectedDish.images.length > 0 && (
                      <div className="mt-2 mb-3 flex flex-wrap gap-2">
                        {selectedDish.images.map((img, idx) => (
                          <div key={idx} className="relative inline-block">
                            <img
                              src={img}
                              alt={`${selectedDish.name}-${idx}`}
                              className="h-20 w-20 object-cover rounded-md border"
                              onError={(e) => { e.target.src = '/api/placeholder/120/120'; }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <input
                      type="file"
                      name="image"
                      onChange={handleFormChange}
                      accept="image/*"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {removeCurrentImage && (
                      <div className="mt-2 text-xs text-red-600">
                        Текущее изображение будет удалено при сохранении.
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_available"
                        checked={formData.is_available}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Доступно для заказа
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_vegetarian"
                        checked={formData.is_vegetarian}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        🌱 Вегетарианское
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_spicy"
                        checked={formData.is_spicy}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        🌶️ Острое
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_new"
                        checked={formData.is_new}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        ✨ Новинка
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_popular"
                        checked={formData.is_popular}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        🔥 Популярное
                      </label>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {updating && <FaSpinner className="animate-spin" />}
                      {modalType === 'add' ? 'Создать' : 'Сохранить'}
                    </button>
                  </div>
                </form>
              )}

              {/* Форма редактирования категории */}
              {modalType === 'editCategory' && (
                <form onSubmit={handleUpdateCategory} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Название *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={categoryFormData.name}
                        onChange={handleCategoryFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Заведение *
                      </label>
                      <select
                        name="establishment"
                        value={categoryFormData.establishment}
                        onChange={handleCategoryFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Выберите заведение</option>
                        {Array.isArray(establishments) && establishments.map(establishment => (
                          <option key={establishment.id} value={establishment.id}>
                            {establishment.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Описание
                    </label>
                    <textarea
                      name="description"
                      value={categoryFormData.description}
                      onChange={handleCategoryFormChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Порядок сортировки
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={categoryFormData.order}
                      onChange={handleCategoryFormChange}
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={categoryFormData.is_active}
                      onChange={handleCategoryFormChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Активная категория
                    </label>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {updating && <FaSpinner className="animate-spin" />}
                      Сохранить изменения
                    </button>
                  </div>
                </form>
              )}

              {/* Форма категории */}
              {modalType === 'category' && (
                <form onSubmit={handleSubmitCategory} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Название *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={categoryFormData.name}
                        onChange={handleCategoryFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Заведение *
                      </label>
                      <select
                        name="establishment"
                        value={categoryFormData.establishment}
                        onChange={handleCategoryFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Выберите заведение</option>
                        {Array.isArray(establishments) && establishments.map(establishment => (
                          <option key={establishment.id} value={establishment.id}>
                            {establishment.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Описание
                    </label>
                    <textarea
                      name="description"
                      value={categoryFormData.description}
                      onChange={handleCategoryFormChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Порядок сортировки
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={categoryFormData.order}
                      onChange={handleCategoryFormChange}
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={categoryFormData.is_active}
                      onChange={handleCategoryFormChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Активная категория
                    </label>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {updating && <FaSpinner className="animate-spin" />}
                      Создать категорию
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagement; 